import { useToken } from '@ori/auth';
import { PbsApp } from '@ori/personal-beauty-store';
import type {
  PbsAppProps,
  RemoveQueryParameterCallback,
  SetQueryParameterCallback,
} from '@ori/personal-beauty-store';
import { Team, TestIdProvider } from '@ori/testid-generator';
import type { VFC } from 'react';
import { Suspense, useMemo } from 'react';

import { TopAreaBlock } from '../components/TopAreaBlock';
import { ApiProviderContextProvider } from '../contexts/ApiProviderContext';
import { AppConfigurationProvider } from '../contexts/AppConfigurationContext';
import { CataloguesContextProvider } from '../contexts/CataloguesContext';
import { NavigationContextProvider } from '../contexts/NavigationContext';
import { NotificationsContextProvider } from '../contexts/NotificationsContext';
import { WindowDimensionsProvider } from '../contexts/WindowDimensionsContext';
import type { AppConfiguration, CombinedTranslations } from '../models';
import { I18nContainer } from './I18nContainer';
import { LazyTopArea } from './LazyTopArea';
import { TopAreaSkeleton } from '~/components/Skeleton/TopAreaSkeleton';

export type TopAreaAppProps = AppConfiguration & {
  /**
   * Preloaded translations for this app
   */
  translations?: CombinedTranslations;

  removeQueryParameter?: RemoveQueryParameterCallback;
  setQueryParameter?: SetQueryParameterCallback;
};

export const TopAreaApp: VFC<TopAreaAppProps> = (config) => {
  const {
    translations: preloadedTranslations,
    pbsIconInBurgerMenu,
    removeQueryParameter,
    setQueryParameter,
  } = config;
  const { customerId } = useToken();

  const pbsConfiguration = useMemo((): PbsAppProps['configuration'] => {
    const { translations: _, ...restConfig } = config;

    return {
      ...restConfig,
      isEnabled: Boolean(pbsIconInBurgerMenu),
      customerId,
    };
  }, [config, pbsIconInBurgerMenu, customerId]);

  return (
    <AppConfigurationProvider config={config}>
      <ApiProviderContextProvider>
        <TestIdProvider
          team={Team.Presentation}
          project="top-area"
        >
          <WindowDimensionsProvider>
            <NavigationContextProvider>
              <NotificationsContextProvider>
                <CataloguesContextProvider>
                  <Suspense fallback={<TopAreaSkeleton />}>
                    <I18nContainer preloadedTranslations={preloadedTranslations}>
                      <PbsApp
                        configuration={pbsConfiguration}
                        removeQueryParameter={removeQueryParameter}
                        setQueryParameter={setQueryParameter}
                      >
                        <TopAreaBlock>
                          <LazyTopArea />
                        </TopAreaBlock>
                      </PbsApp>
                    </I18nContainer>
                  </Suspense>
                </CataloguesContextProvider>
              </NotificationsContextProvider>
            </NavigationContextProvider>
          </WindowDimensionsProvider>
        </TestIdProvider>
      </ApiProviderContextProvider>
    </AppConfigurationProvider>
  );
};
